import * as React from "react";

import type {
  BedrockCatalogModel,
  BedrockModelRoleBinding,
} from "@/shared/domain/orgs";
import {
  useBedrockCatalog,
  useBedrockRoles,
  useUpdateBedrockRole,
} from "@/shared/domain/orgs/hooks";
import { RobotoButton, Select, Spinner } from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http/userFacingErrorMessage";

import styles from "./AiModels.module.css";

/** Select value standing for `null`: the role is unset. Not "", which the Select reads as no value. */
const NONE = "__none__";

/**
 * Which Bedrock catalog entry does each deployment-wide job: titles and
 * compression, knowledge extraction and video analysis. One row per role, saved on its own.
 */
export function BedrockModelRoles() {
  const rolesQuery = useBedrockRoles();
  const catalogQuery = useBedrockCatalog();

  return (
    <section className={styles.roles} aria-labelledby="bedrock-model-roles">
      <h3 id="bedrock-model-roles" className={styles.sectionTitle}>
        Model roles
      </h3>
      <p className={styles.note}>
        The catalog entry each deployment-wide job runs on. Only verified,
        priced entries can be picked.
      </p>
      {rolesQuery.isLoading && <Spinner aria-label="Loading model roles" />}
      {rolesQuery.error && (
        <p className={styles.error} role="alert">
          {userFacingErrorMessage(rolesQuery.error)}
        </p>
      )}
      <div className={styles.roleRows}>
        {rolesQuery.data?.map((binding) => (
          <BedrockModelRoleRow
            // Re-keyed on the bound model so a saved (or refetched) binding
            // resets the row's unsaved choice.
            key={`${binding.role}:${binding.model ?? NONE}`}
            binding={binding}
            catalog={catalogQuery.data ?? []}
          />
        ))}
      </div>
    </section>
  );
}

function BedrockModelRoleRow({
  binding,
  catalog,
}: {
  binding: BedrockModelRoleBinding;
  catalog: readonly BedrockCatalogModel[];
}) {
  const updateMutation = useUpdateBedrockRole();
  const [draft, setDraft] = React.useState(binding.model ?? NONE);

  const items = roleItems(binding, catalog);
  // Only "None" (and a bound entry kept for its name) when nothing qualifies.
  const noImageModels =
    binding.role === "vision" && !catalog.some((m) => isEligible(binding, m));
  const changed = draft !== (binding.model ?? NONE);

  return (
    <div className={styles.roleRow}>
      <Select<string>
        className={styles.roleSelect}
        label={binding.label}
        description={binding.description}
        items={items}
        value={draft}
        onValueChange={(value) => setDraft(value ?? NONE)}
        disabled={updateMutation.isPending}
        placeholder="Choose a model"
      />
      <RobotoButton
        size="sm"
        aria-label={`Save ${binding.label}`}
        disabled={!changed}
        loading={updateMutation.isPending}
        onClick={() =>
          updateMutation.mutate({
            role: binding.role,
            model: draft === NONE ? null : draft,
          })
        }
      >
        Save
      </RobotoButton>
      {noImageModels && (
        <p className={styles.note}>
          No verified, priced entry takes images. Turn on image support in an
          entry&apos;s Vision section first.
        </p>
      )}
      {updateMutation.isError && (
        <p className={styles.error} role="alert">
          {userFacingErrorMessage(updateMutation.error)}
        </p>
      )}
    </div>
  );
}

/**
 * Whether `model` may do the role: verified and priced, and for the vision
 * role, taking images.
 */
function isEligible(
  binding: BedrockModelRoleBinding,
  model: BedrockCatalogModel,
): boolean {
  return (
    model.verified &&
    !model.needs_price &&
    (binding.role !== "vision" || model.vision.supports_images)
  );
}

/** Why a bound entry no longer qualifies for its role, as the picker names it. */
function ineligibleSuffix(model: BedrockCatalogModel): string {
  return model.verified && !model.needs_price
    ? "(takes no images)"
    : "(unverified)";
}

/**
 * The picker's choices: "None" for an optional role, then every eligible
 * catalog entry (see {@link isEligible}). The bound entry is kept even when
 * it no longer qualifies (since unverified, or no longer taking images), so
 * the picker still names it.
 */
function roleItems(
  binding: BedrockModelRoleBinding,
  catalog: readonly BedrockCatalogModel[],
) {
  const eligible = catalog.filter((m) => isEligible(binding, m));
  const items = eligible.map((m) => ({ value: m.id, label: m.label }));
  if (binding.model !== null && !eligible.some((m) => m.id === binding.model)) {
    const bound = catalog.find((m) => m.id === binding.model);
    items.unshift({
      value: binding.model,
      // Ids are never shown: a bound entry the catalog no longer lists is
      // named for what it is.
      label:
        bound === undefined
          ? "A model no longer in the catalog"
          : `${bound.label} ${ineligibleSuffix(bound)}`,
    });
  }
  return binding.required ? items : [{ value: NONE, label: "None" }, ...items];
}
